import type { WavesChart } from '@shared/wavesChart';
import type {
  ReplayOutcome,
  WavesReplayInput,
  WavesReplayInputs,
} from '@shared/replay';
import {
  step,
  type WavesEngineEvent,
  type WavesEngineInput,
} from '@shared/engine/waves/core';
import {
  buildWavesEngineConfig,
  createInitialWavesState,
  resetWavesStateForNextLoop,
  type WavesEngineConfig,
  type WavesEngineState,
} from '@shared/engine/waves/state';
import { computeAccuracy } from '@shared/scoring';
import {
  milestoneIsStar,
  milestoneTier,
  type MilestoneKind,
} from '@shared/progression';
import { AudioEngine, clampPlaybackRate } from '../audio/scheduler';
import { loadVolumePrefs } from '../audio/volumePrefs';
import { LoopRunState } from '../loopManager';
import { RunTimeline } from '../input/runTimeline';
import type { RunTimingReport } from '@shared/antiCheat/runTiming';
import {
  createEmptyWavesShake,
  WavesRenderer,
} from './wavesRenderer';
import {
  KeyboardWavesInputSource,
  ReplayWavesInputSource,
  type WavesInputSource,
} from './wavesInputSource';
import {
  CLOSE_CALL_CLEARANCE,
  CLOSE_CALL_COLOUR,
  COMBO_MILESTONES,
  DEATH_COLOUR,
  FLOATING_TEXT_LIFE,
  FLOATING_TEXT_LIFE_BIG,
  HIT_STOP_MS_CLOSE,
  HIT_STOP_MS_DEATH,
  HIT_STOP_MS_PASS,
  MILESTONE_COLOUR,
  SCORE_POP_LIFE,
  VERDICT_COLOURS,
} from './render/wavesRenderConstants';
import type {
  WavesFlashSpec,
  WavesFloatingText,
  WavesParticleEmission,
  WavesScorePop,
  WavesShakeSpec,
  WavesShockwaveSpec,
} from './render/wavesRenderTypes';

export type WavesSessionStatus =
  | 'idle'
  | 'starting'
  | 'lead_in'
  | 'running'
  | 'paused'
  | 'awaiting_next_loop'
  | 'completed'
  | 'failed';

export interface WavesMilestoneInfo {
  kind: MilestoneKind;
  loopIndex: number;
  starsEarned: number;
  cupsEarned: number;
  pause: boolean;
}

export interface WavesGameSessionOptions {
  chart: WavesChart;
  canvas: HTMLCanvasElement;
  audioOffsetMs: number;
  /** Pre-recorded inputs (for replay verifier / debug). */
  replayInputs?: WavesEngineInput[];
  /** Optional human-readable key label shown in the HUD ("SPACE", "TAP"). */
  keyLabel?: string;
  onStatusChange?: (status: WavesSessionStatus) => void;
  onComplete?: (outcome: ReplayOutcome) => void;
  onEvent?: (event: WavesEngineEvent) => void;
  onMilestone?: (info: WavesMilestoneInfo) => void;
  testHook?: boolean;
  enableLoops?: boolean;
}

const LEAD_IN_BEATS = 4;
const MIN_LEAD_IN_WALL_SECONDS = 2.0;
/** Eased score lerp factor; same constant Circles uses for HUD readout. */
const SCORE_DISPLAY_LERP_RATE = 15;

function effectiveLeadInBeats(baseBeats: number, bpm: number, startSpeed: number): number {
  const effectiveSpeed = clampPlaybackRate(startSpeed);
  const wallTime = (baseBeats * 60) / bpm / effectiveSpeed;
  if (wallTime >= MIN_LEAD_IN_WALL_SECONDS) {
    return baseBeats;
  }
  return Math.ceil((MIN_LEAD_IN_WALL_SECONDS * bpm * effectiveSpeed) / 60);
}

/**
 * Waves-mode game session. Mirrors {@link import('../session.js').GameSession}'s lifecycle
 * (constructor → start() → tick loop → pause/resume → destroy) so the
 * Waves page can be paralleled cleanly with Pulse/Circles/Guitar pages.
 *
 * Composition only — engines, renderers, audio, and input are
 * dependency-injected, never inherited. The session is the single
 * "conductor" that pulls audio time forward, polls inputs, steps the
 * shared engine, fans out events into renderer FX queues, and feeds
 * the renderer once per frame.
 *
 * Differences vs Pulse/Circles:
 *   - One-shot fail model — no HP bar, any collision ends the run.
 *   - No per-obstacle judgement tiers — every clean pass scores
 *     identically (combo multiplier is the only score modulator).
 *   - Reuses LoopRunState verbatim — star/cup gates fire on the same
 *     beat fractions as every other mode.
 */
export class WavesGameSession {
  private chart: WavesChart;
  private engineConfig: WavesEngineConfig;
  private state: WavesEngineState;
  private audio: AudioEngine;
  private renderer: WavesRenderer;
  private inputSource: WavesInputSource;
  /** Anti-cheat timing recorder (real-time progression + isTrusted). */
  private timeline: RunTimeline;
  private particleEmissions: WavesParticleEmission[] = [];
  private floatingTexts: WavesFloatingText[] = [];
  private scorePops: WavesScorePop[] = [];
  private flashes: WavesFlashSpec[] = [];
  private shockwaveSpecs: WavesShockwaveSpec[] = [];
  private shake: WavesShakeSpec = createEmptyWavesShake();
  private currentBeatPulse = 0;
  private edgeAnim = 0;
  private rafId: number | null = null;
  private lastBeat = 0;
  private status: WavesSessionStatus = 'idle';
  private opts: WavesGameSessionOptions;
  private outcome: ReplayOutcome | null = null;
  private allEvents: WavesEngineEvent[] = [];
  private loopCapturedInputs: WavesReplayInput[][] = [[]];
  private comboMilestonesHit = new Set<number>();
  private lastTickAt = performance.now();
  private loopState: LoopRunState;
  private enableLoops: boolean;
  private destroyed = false;
  private autoPaused = false;
  private displayedScore = 0;
  private cachedRect = { width: 0, height: 0 };
  private resizeObserver: ResizeObserver | null = null;

  constructor(opts: WavesGameSessionOptions) {
    this.opts = opts;
    this.chart = opts.chart;
    this.enableLoops = opts.enableLoops ?? false;
    this.loopState = new LoopRunState(this.chart.totalBeats);
    this.engineConfig = buildWavesEngineConfig(this.chart);
    this.state = createInitialWavesState(this.chart);
    this.audio = new AudioEngine({ audioOffsetMs: opts.audioOffsetMs });
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
    this.renderer = new WavesRenderer(opts.canvas);
    this.attachResizeObserver();

    if (opts.replayInputs) {
      this.inputSource = new ReplayWavesInputSource(opts.replayInputs);
    } else {
      this.inputSource = new KeyboardWavesInputSource(
        () => this.audio.getCurrentBeat(),
        () => this.status === 'lead_in' || this.status === 'running',
      );
    }

    this.timeline = new RunTimeline({
      getBeatAtNow: () => this.audio.getCurrentBeat(),
      isClockRunning: () => this.status === 'running',
    });
    this.timeline.attach();

    if (opts.testHook) {
      (window as unknown as {
        __beatHeavenWavesTestHook?: WavesGameSessionTestHook;
      }).__beatHeavenWavesTestHook = this.buildTestHook();
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      if (this.status === 'running' || this.status === 'lead_in') {
        this.autoPaused = true;
        this.pause();
      }
    } else if (this.autoPaused && this.status === 'paused') {
      this.autoPaused = false;
      this.resume().catch((err) => {
        console.warn('Auto-resume after tab return failed:', err);
      });
    }
  };

  async start(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.start(this.chart, leadIn, { speedRamp: ramp });
    if (this.destroyed) {
      return;
    }
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.tick();
  }

  async startNextLoop(): Promise<void> {
    if (this.destroyed || this.status !== 'awaiting_next_loop') {
      return;
    }
    this.loopState.advanceLoop();
    resetWavesStateForNextLoop(this.state, this.chart);
    this.lastBeat = 0;
    this.comboMilestonesHit.clear();
    this.loopCapturedInputs.push([]);
    this.timeline.startLoop(this.loopCapturedInputs.length - 1);
    this.flashes.length = 0;
    this.shockwaveSpecs.length = 0;
    this.particleEmissions.length = 0;
    this.renderer.clearTrail();
    // Hand the input source to the new loop's clock: drop any presses
    // still queued from the loop that just ended (their beats are in the
    // old clock's space and would corrupt this loop's capture), and — if
    // the player held the button through the loop-finale overlays —
    // re-queue a synthetic `down` so the reset engine matches the
    // still-held key the OS won't re-announce.
    this.inputSource.rehomeForLoopStart(0);

    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.restartForLoop(this.chart, leadIn, ramp);
    if (this.destroyed) {
      return;
    }
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.lastTickAt = performance.now();
    this.tick();
  }

  getCurrentBeat(): number {
    return this.audio.getCurrentBeat();
  }

  pause(): void {
    if (this.status === 'running' || this.status === 'lead_in') {
      this.setStatus('paused');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      void this.audio.suspend().catch((err) => {
        console.warn('Failed to suspend audio on pause:', err);
      });
    }
  }

  manualPause(): void {
    this.autoPaused = false;
    this.pause();
  }

  async resume(): Promise<void> {
    if (this.status === 'paused') {
      this.autoPaused = false;
      await this.audio.resume();
      this.setStatus('running');
      this.tick();
    }
  }

  async destroy(): Promise<void> {
    this.destroyed = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.inputSource.destroy();
    this.timeline.detach();
    this.renderer.resetAll();
    await this.audio.stop();
    delete (window as unknown as {
      __beatHeavenWavesTestHook?: WavesGameSessionTestHook;
    }).__beatHeavenWavesTestHook;
  }

  getOutcome(): ReplayOutcome | null {
    return this.outcome;
  }

  getStatus(): WavesSessionStatus {
    return this.status;
  }

  getCapturedReplay(): WavesReplayInputs {
    return {
      loops: this.loopCapturedInputs.map((inputs) => ({ inputs: inputs.slice() })),
      audioOffsetMs: this.opts.audioOffsetMs,
    };
  }

  /** Anti-cheat timing stream for this run (null if nothing captured). */
  getTimingReport(): RunTimingReport | null {
    return this.timeline.toReport();
  }

  getProgressionEarnedThisRun(): { stars: number; cups: number } {
    return {
      stars: this.loopState.countStars(),
      cups: this.loopState.countCups(),
    };
  }

  getLoopIndex(): number {
    return this.loopState.getLoopIndex();
  }

  getLoopsCleared(): number {
    return this.loopState.getLoopIndex();
  }

  applyVolumePrefs(): void {
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
  }

  private setStatus(s: WavesSessionStatus): void {
    if (this.status === s) {
      return;
    }
    this.status = s;
    this.opts.onStatusChange?.(s);
  }

  private tick = (): void => {
    if (this.status === 'paused') {
      return;
    }

    const currentBeat = this.audio.getCurrentBeat();
    if (!Number.isFinite(currentBeat)) {
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }
    const now = performance.now();
    const dt = Math.min(0.05, Math.max(0, (now - this.lastTickAt) / 1000));
    this.lastTickAt = now;

    if (currentBeat < 0) {
      const leadInBeat = this.computeLeadInBeat();
      this.tickBeatPulse(currentBeat, dt);
      this.drawFrame(currentBeat, leadInBeat);
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    if (this.status === 'lead_in') {
      this.setStatus('running');
    }

    this.tickShake(dt);
    this.tickBeatPulse(currentBeat, dt);
    this.tickEdgeAnim(dt);

    const cappedTarget = Math.min(currentBeat, this.lastBeat + this.audio.maxAdvanceBeatsPerFrame());

    const rawInputs = this.inputSource.poll(cappedTarget);
    // Lead-in presses arrive with negative beats (audio clock hadn't
    // reached zero yet). Clamp them to 0 instead of dropping so the
    // engine actually sees the player's "hold-into-bar-1" intent —
    // dropping them desyncs the renderer's `isPressing()` (still true)
    // from the engine's `state.pressing` (still false).
    const clampedInputs: WavesEngineInput[] = rawInputs.map((inp) => ({
      beat: Math.max(this.lastBeat, Math.min(Math.max(inp.beat, 0), cappedTarget)),
      type: inp.type,
    }));
    const activeLoopCapture = this.loopCapturedInputs[this.loopCapturedInputs.length - 1];
    for (const inp of clampedInputs) {
      activeLoopCapture.push({ beat: inp.beat, type: inp.type });
    }

    const out = step({
      config: this.engineConfig,
      state: this.state,
      fromBeat: this.lastBeat,
      toBeat: cappedTarget,
      inputs: clampedInputs,
    });
    this.lastBeat = Math.max(this.lastBeat, cappedTarget);
    for (const ev of out.events) {
      this.handleEvent(ev);
      this.allEvents.push(ev);
      this.opts.onEvent?.(ev);
    }

    this.detectAndFireMidRunMilestones();
    this.tickDisplayedScore(dt);

    this.drawFrame(this.state.beat, null);

    if (!this.state.alive && this.status !== 'failed') {
      this.finishSession('failed');
      return;
    }
    if (this.state.completed) {
      this.handleLoopComplete();
      return;
    }

    this.rafId = requestAnimationFrame(this.tick);
  };

  private tickShake(dt: number): void {
    if (this.shake.amount > 0) {
      this.shake.amount = Math.max(0, this.shake.amount - this.shake.decay * dt);
      if (this.shake.amount < 0.01) {
        this.shake.biasY = undefined;
      }
    }
  }

  private tickBeatPulse(currentBeat: number, dt: number): void {
    const beatFrac = ((currentBeat % 1) + 1) % 1;
    if (beatFrac < 0.08) {
      this.currentBeatPulse = 1;
    } else {
      const beatsPerSec = this.chart.bpm / 60;
      const decayPerSec = beatsPerSec * 1.6;
      this.currentBeatPulse = Math.max(0, this.currentBeatPulse - decayPerSec * dt);
    }
  }

  private tickEdgeAnim(dt: number): void {
    if (this.edgeAnim > 0) {
      this.edgeAnim = Math.max(0, this.edgeAnim - dt * 5);
    }
  }

  private tickDisplayedScore(dt: number): void {
    const target = this.state.score;
    if (this.displayedScore < target) {
      const k = 1 - Math.exp(-SCORE_DISPLAY_LERP_RATE * dt);
      this.displayedScore += (target - this.displayedScore) * k;
      if (target - this.displayedScore < 0.5) {
        this.displayedScore = target;
      }
    } else if (this.displayedScore > target) {
      // Score doesn't decrease mid-loop, but the loop reset path zeros
      // it on the engine side. Snap rather than animate backwards.
      this.displayedScore = target;
    }
  }

  private detectAndFireMidRunMilestones(): void {
    if (!this.enableLoops) {
      return;
    }
    const candidates = this.loopState.detectMidRunMilestones(this.state.beat);
    for (const kind of candidates) {
      if (this.loopState.markFired(kind)) {
        this.spawnMilestoneFx(kind);
        this.opts.onMilestone?.({
          kind,
          loopIndex: this.loopState.getLoopIndex(),
          starsEarned: this.loopState.countStars(),
          cupsEarned: this.loopState.countCups(),
          pause: false,
        });
      }
    }
  }

  private handleLoopComplete(): void {
    const completionMilestone = this.enableLoops
      ? this.loopState.detectCompletionMilestone()
      : null;
    if (completionMilestone && this.loopState.markFired(completionMilestone)) {
      this.spawnMilestoneFx(completionMilestone);
      const isLoopOneFinale = this.loopState.getLoopIndex() === 0;
      this.opts.onMilestone?.({
        kind: completionMilestone,
        loopIndex: this.loopState.getLoopIndex(),
        starsEarned: this.loopState.countStars(),
        cupsEarned: this.loopState.countCups(),
        pause: isLoopOneFinale,
      });
    }
    if (this.enableLoops) {
      this.setStatus('awaiting_next_loop');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      const isLoopOneFinale = this.loopState.getLoopIndex() === 0;
      if (isLoopOneFinale) {
        return;
      }
      void this.startNextLoop();
      return;
    }
    if (this.status !== 'completed') {
      this.finishSession('completed');
    }
  }

  private computeLeadInBeat(): number | null {
    const songTime = this.audio.getSongTime();
    if (!Number.isFinite(songTime)) {
      return null;
    }
    if (songTime >= 0) {
      return null;
    }
    const beatsRemaining = -songTime * (this.chart.bpm / 60);
    return Math.max(0, beatsRemaining);
  }

  private attachResizeObserver(): void {
    const rect = this.opts.canvas.getBoundingClientRect();
    this.cachedRect.width = rect.width;
    this.cachedRect.height = rect.height;
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      this.cachedRect.width = entry.contentRect.width;
      this.cachedRect.height = entry.contentRect.height;
    });
    this.resizeObserver.observe(this.opts.canvas);
  }

  private drawFrame(beat: number, leadInBeat: number | null): void {
    this.renderer.draw({
      chart: this.chart,
      state: this.state,
      beat,
      width: this.cachedRect.width,
      height: this.cachedRect.height,
      pressing: this.inputSource.isPressing(),
      displayedScore: this.displayedScore,
      countdown: leadInBeat,
      floatingTexts: this.floatingTexts,
      scorePops: this.scorePops,
      particleEmissions: this.particleEmissions,
      shake: this.shake,
      flashes: this.flashes,
      shockwaveSpecs: this.shockwaveSpecs,
      beatPulse: this.currentBeatPulse,
      edgeAnim: this.edgeAnim,
      keyLabel: this.opts.keyLabel ?? 'SPACE',
    });
  }

  private spawnFloatingText(
    text: string,
    x: number,
    y: number,
    colour: string,
    opts: { big?: boolean; vy?: number; scale?: number } = {},
  ): void {
    const big = opts.big ?? false;
    const life = big ? FLOATING_TEXT_LIFE_BIG : FLOATING_TEXT_LIFE;
    const jitterX = big ? 0 : (Math.random() - 0.5) * 22;
    this.floatingTexts.push({
      text,
      x: x + jitterX,
      y: y - 28,
      colour,
      vy: opts.vy ?? -55,
      scale: opts.scale ?? 1,
      life,
      maxLife: life,
      big,
    });
  }

  private spawnScorePop(
    anchorX: number,
    anchorY: number,
    scoreDelta: number,
    verdict: 'perfect' | 'good',
  ): void {
    if (scoreDelta <= 0) {
      return;
    }
    const big = scoreDelta >= 500;
    const target = this.renderer.getScoreHudAnchor();
    this.scorePops.push({
      text: `+${scoreDelta}`,
      startX: anchorX,
      startY: anchorY,
      targetX: target.x,
      targetY: target.y,
      colour: verdict === 'perfect' ? VERDICT_COLOURS.perfect : VERDICT_COLOURS.good,
      big,
      life: SCORE_POP_LIFE,
      maxLife: SCORE_POP_LIFE,
    });
  }

  private addShake(amount: number, biasY?: number): void {
    this.shake.amount = Math.max(this.shake.amount, amount);
    this.shake.decay = amount * 6;
    if (biasY !== undefined) {
      this.shake.biasY = biasY;
    }
  }

  private pushShockwave(
    x: number,
    y: number,
    colour: string,
    opts: { startR?: number; endR?: number; life?: number; alpha?: number } = {},
  ): void {
    this.shockwaveSpecs.push({
      x,
      y,
      startR: opts.startR ?? 24,
      endR: opts.endR ?? 95,
      life: opts.life ?? 0.38,
      thickness: 3,
      colour,
      alpha: opts.alpha ?? 0.75,
    });
  }

  private spawnMilestoneFx(kind: MilestoneKind): void {
    const isStar = milestoneIsStar(kind);
    const tier = milestoneTier(kind);
    const colour = isStar ? MILESTONE_COLOUR : '#ffb13b';
    const anchor = this.renderer.getPlayerAnchor(this.state);
    this.particleEmissions.push({
      type: 'milestone',
      x: anchor.x,
      y: anchor.y,
      colour,
      tier,
    });
    const glyph = isStar ? '\u2605' : '\u{1F3C6}';
    this.spawnFloatingText(`${glyph} x${tier}`, anchor.x, anchor.y, colour, {
      big: true,
      vy: -90,
      scale: tier === 3 ? 1.4 : 1.15,
    });
    this.addShake(tier === 3 ? 14 : 6 + tier * 2);
    this.flashes.push({
      type: 'milestone',
      t: 0.35,
      intensity: tier === 3 ? 1.4 : 1,
      colour,
    });
    this.pushShockwave(anchor.x, anchor.y, colour, {
      startR: 32,
      endR: tier === 3 ? 140 : 110,
      life: 0.5,
      alpha: 0.85,
    });
    this.audio.playMilestoneFanfare(tier);
    if (kind === 'star_3') {
      this.audio.duckMusic(0.12, 350);
      this.audio.playVictorySting();
    }
  }

  private handleEvent(ev: WavesEngineEvent): void {
    if (ev.type === 'obstacle_passed') {
      const anchor = this.renderer.getObstacleAnchor(
        this.chart,
        this.state,
        ev.beat,
        this.state.beat,
      );
      const verdict = ev.payload.verdict;
      const verdictColour =
        verdict === 'perfect' ? VERDICT_COLOURS.perfect : VERDICT_COLOURS.good;
      this.particleEmissions.push({
        type: 'obstacle_pass',
        x: anchor.x,
        y: anchor.y,
        colour: verdictColour,
      });
      this.pushShockwave(anchor.x, anchor.y, verdictColour, {
        startR: 26,
        endR: 100,
        life: 0.4,
        alpha: 0.7,
      });
      this.flashes.push({
        type: verdict === 'perfect' ? 'pass' : 'good',
        t: 0.16,
        intensity: verdict === 'perfect' ? 0.55 : 0.42,
      });
      this.renderer.requestHitStop(HIT_STOP_MS_PASS);
      this.spawnScorePop(anchor.x, anchor.y, ev.payload.scoreDelta, verdict);
      const verdictTextAnchor = this.renderer.getVerdictTextAnchor(this.state);
      this.spawnFloatingText(
        verdict === 'perfect' ? 'PERFECT!' : 'GOOD',
        verdictTextAnchor.x,
        verdictTextAnchor.y,
        verdictColour,
        { scale: verdict === 'perfect' ? 1.05 : 0.95 },
      );
      this.audio.scheduleHitFeedback(verdict);

      const lastResolution = this.state.resolutions[this.state.resolutions.length - 1];
      const isCloseCall =
        verdict === 'perfect' &&
        lastResolution &&
        lastResolution.obstacleId === ev.payload.obstacleId &&
        lastResolution.clearanceY < CLOSE_CALL_CLEARANCE;
      if (isCloseCall) {
        this.particleEmissions.push({
          type: 'obstacle_pass',
          x: anchor.x,
          y: anchor.y,
          colour: CLOSE_CALL_COLOUR,
        });
        this.pushShockwave(anchor.x, anchor.y, CLOSE_CALL_COLOUR, {
          startR: 18,
          endR: 80,
          life: 0.32,
          alpha: 0.9,
        });
        this.spawnFloatingText(
          'CLOSE!',
          verdictTextAnchor.x,
          verdictTextAnchor.y,
          CLOSE_CALL_COLOUR,
          { vy: -110, scale: 0.9 },
        );
        this.flashes.push({ type: 'close', t: 0.2, intensity: 0.85 });
        this.renderer.requestHitStop(HIT_STOP_MS_CLOSE);
        this.addShake(5);
      }

      for (const milestone of COMBO_MILESTONES) {
        if (this.state.combo === milestone && !this.comboMilestonesHit.has(milestone)) {
          this.comboMilestonesHit.add(milestone);
          const playerAnchor = this.renderer.getPlayerAnchor(this.state);
          this.spawnFloatingText(
            `x${milestone} COMBO!`,
            playerAnchor.x,
            playerAnchor.y,
            '#ff2bd6',
            { big: true, vy: -80 },
          );
          this.pushShockwave(playerAnchor.x, playerAnchor.y, '#ff2bd6', {
            startR: 40,
            endR: 130,
            life: 0.55,
            alpha: 0.8,
          });
          this.flashes.push({ type: 'close', t: 0.28, intensity: 1.1 });
          this.audio.scheduleComboMilestone();
          this.addShake(3);
        }
      }
    } else if (ev.type === 'death') {
      const anchor = this.renderer.getPlayerAnchor(this.state);
      this.particleEmissions.push({
        type: 'death',
        x: anchor.x,
        y: anchor.y,
        colour: DEATH_COLOUR,
      });
      this.pushShockwave(anchor.x, anchor.y, DEATH_COLOUR, {
        startR: 36,
        endR: 120,
        life: 0.45,
        alpha: 0.9,
      });
      this.spawnFloatingText('GAME OVER', anchor.x, anchor.y, DEATH_COLOUR, {
        big: true,
        vy: -30,
      });
      const deathBiasY = this.state.pressing ? -1 : 1;
      this.addShake(14, deathBiasY);
      this.flashes.push({ type: 'death', t: 0.55, intensity: 1.2 });
      this.renderer.requestHitStop(HIT_STOP_MS_DEATH);
      this.audio.playDeath();
      this.audio.duckMusicPulse(0.5, 40, 280);
    } else if (ev.type === 'edge') {
      this.edgeAnim = 1;
      this.flashes.push({ type: 'edge', t: 0.1, intensity: 0.35 });
      this.audio.scheduleWavesEdge(ev.payload.pressing);
    } else if (ev.type === 'completed') {
      // The loop wrap is handled by handleLoopComplete(); the event is
      // emitted purely so analytics + replay logs see the boundary.
    }
  }

  private finishSession(status: WavesSessionStatus): void {
    this.setStatus(status);
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const loopsCleared = this.getLoopsCleared();
    this.outcome = {
      completed: this.state.completed || loopsCleared >= 1,
      score: this.state.score,
      combo: this.state.combo,
      maxCombo: this.state.maxCombo,
      perfects: this.state.perfects,
      goods: this.state.goods,
      oks: this.state.oks,
      misses: this.state.misses,
      accuracy: computeAccuracy(
        this.state.perfects,
        this.state.goods,
        this.state.oks,
        this.state.misses,
      ),
      deathBeat: this.state.deathBeat,
      deathReason: this.state.deathReason ?? undefined,
    };
    this.audio.stopMusic();
    if (this.state.completed && this.state.score > 0) {
      this.audio.playVictorySting();
    } else if (!this.state.completed && loopsCleared === 0) {
      this.audio.playFailureSting();
    }
    this.opts.onComplete?.(this.outcome);
  }

  private buildTestHook(): WavesGameSessionTestHook {
    return {
      getOutcome: () => this.outcome,
      getStatus: () => this.status,
      getState: () => this.state,
      getBeat: () => this.audio.getCurrentBeat(),
      getEvents: () => this.allEvents.slice(),
      isAudioStarted: () => this.audio.isStarted,
    };
  }
}

export interface WavesGameSessionTestHook {
  getOutcome: () => ReplayOutcome | null;
  getStatus: () => WavesSessionStatus;
  getState: () => WavesEngineState;
  getBeat: () => number;
  getEvents: () => WavesEngineEvent[];
  isAudioStarted: () => boolean;
}
